import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { WorkItem } from '@rapider/sdk';
import { WorkItemFilters, WorkitemsActions } from './workitems.actions';

export const workitemsFeatureKey = 'workitems';

export interface WorkitemsState {
  workitems: WorkItem[];
  selectedWorkitemId: string | null;
  workitemDetails: Record<string, WorkItem>;
  isLoading: boolean;
  activeFilters: WorkItemFilters | null;
  error: string | null;
}

export const initialWorkitemsState: WorkitemsState = {
  workitems: [],
  selectedWorkitemId: null,
  workitemDetails: {},
  isLoading: false,
  activeFilters: null,
  error: null,
};

export const workitemsFeature = createFeature({
  name: workitemsFeatureKey,
  reducer: createReducer(
    initialWorkitemsState,

    // Loading workitems: SWR pattern (only flag loading if cache is empty)
    on(WorkitemsActions.loadWorkitems, (state, { filters }) => ({
      ...state,
      isLoading: state.workitems.length === 0,
      activeFilters: filters || null,
      error: null,
    })),

    on(WorkitemsActions.loadWorkitemsSuccess, (state, { workitems }) => {
      const selectedId =
        state.selectedWorkitemId &&
        workitems.some((w) => w.id === state.selectedWorkitemId)
          ? state.selectedWorkitemId
          : workitems.length > 0
          ? workitems[0].id
          : null;

      return {
        ...state,
        workitems,
        selectedWorkitemId: selectedId,
        isLoading: false,
        error: null,
      };
    }),

    on(WorkitemsActions.loadWorkitemsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })),

    // Selecting a workitem
    on(WorkitemsActions.selectWorkitem, (state, { id }) => ({
      ...state,
      selectedWorkitemId: id,
    })),

    // Storing full details of a workitem
    on(WorkitemsActions.loadWorkitemDetailSuccess, (state, { workitem }) => ({
      ...state,
      workitemDetails: {
        ...state.workitemDetails,
        [workitem.id]: workitem,
      },
      // Update in array if present
      workitems: state.workitems.map((w) =>
        w.id === workitem.id ? { ...w, ...workitem } : w,
      ),
    })),

    // Optimistic status update
    on(WorkitemsActions.updateWorkitemStatus, (state, { id, status }) => {
      const updatedList = state.workitems.map((w) =>
        w.id === id ? { ...w, status, needsAttention: false } : w,
      );
      const updatedDetails = state.workitemDetails[id]
        ? {
            ...state.workitemDetails,
            [id]: {
              ...state.workitemDetails[id],
              status,
              needsAttention: false,
            },
          }
        : state.workitemDetails;

      return {
        ...state,
        workitems: updatedList,
        workitemDetails: updatedDetails,
      };
    }),

    on(
      WorkitemsActions.updateWorkitemStatusFailure,
      (state, { id, previousStatus }) => ({
        ...state,
        workitems: state.workitems.map((w) =>
          w.id === id ? { ...w, status: previousStatus } : w,
        ),
        workitemDetails: state.workitemDetails[id]
          ? {
              ...state.workitemDetails,
              [id]: { ...state.workitemDetails[id], status: previousStatus },
            }
          : state.workitemDetails,
      }),
    ),

    // Clearing state
    on(WorkitemsActions.clearWorkitems, () => initialWorkitemsState),
  ),
});

export const {
  selectWorkitemsState,
  selectWorkitems,
  selectSelectedWorkitemId,
  selectWorkitemDetails,
  selectIsLoading: selectIsLoadingWorkitems,
  selectActiveFilters,
  selectError: selectWorkitemsError,
} = workitemsFeature;

/**
 * Resolved selected workitem combining list fields with fetched full details.
 */
export const selectSelectedWorkitem = createSelector(
  selectWorkitems,
  selectSelectedWorkitemId,
  selectWorkitemDetails,
  (items, id, details) => {
    if (!id) return null;
    const detail = details[id];
    if (detail) return detail;
    return items.find((item) => item.id === id) || null;
  },
);
